import { CITY_SLUG, findByNameSlug } from '@/lib/seo';
import { districts, specialties } from '@/mocks/practitioners';

// Domaine canonique de secours, utilisé uniquement si VITE_SITE_URL n'est pas
// défini dans l'environnement. On ne « réinvente » jamais un domaine arbitraire :
// la variable d'environnement reste la source de vérité.
const FALLBACK_SITE_URL = 'https://medicaclic.com';

// Renvoie l'URL racine du site (sans slash final), issue de VITE_SITE_URL.
export function getSiteUrl(): string {
  const raw = import.meta.env.VITE_SITE_URL as string | undefined;
  const value = (raw ?? '').trim();
  if (!value) return FALLBACK_SITE_URL;
  return value.replace(/\/+$/, '');
}

export const SITE_NAME = 'MEDICACLIC';

// Image utilisée pour og:image / twitter:image / logo JSON-LD.
// Il s'agit du favicon existant du projet (URL absolue déjà présente dans index.html).
export const SITE_IMAGE_URL =
  'https://readdy.ai/api/search-image?query=Minimalist%20flat%20app%20icon%2C%20a%20white%20medical%20heart%20with%20a%20clean%20electrocardiogram%20pulse%20line%20running%20horizontally%20through%20it%2C%20centered%20on%20a%20soft%20mint%20green%20teal%20rounded%20square%20background%2C%20simple%20geometric%20flat%20design%2C%20no%20text%2C%20no%20shadow%2C%20high%20contrast%2C%20crisp%20edges%2C%20professional%20healthcare%20brand%20mark%2C%20clean%20flat%20vector%20style&width=512&height=512&seq=favicon-heart-pulse-01&orientation=squarish';

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  // Chemin canonique (sans domaine) ; les paramètres d'URL sont ignorés.
  canonicalPath: string;
  noindex?: boolean;
}

// Pages publiques et indexables de l'application.
export const LEGAL_PATHS = ['/mentions-legales', '/cgu', '/politique-de-confidentialite'];

// SEO de la page d'accueil : valeurs partagées avec index.html (fallback SPA).
export const HOME_SEO: SeoData = {
  title: 'MEDICACLIC — Trouvez un médecin, labo ou pharmacie à Rabat',
  description:
    "MEDICACLIC met en relation patients et praticiens à Rabat : médecins spécialistes, laboratoires d'analyses, radiologie, pharmacies et paramédical. Prise de rendez-vous en ligne, confirmation immédiate.",
  keywords:
    'médecin Rabat, prise de rendez-vous médical Maroc, laboratoire analyses Rabat, pharmacie de garde, radiologie Rabat',
  canonicalPath: '/',
};

// SEO des pages statiques connues.
export const STATIC_SEO: Record<string, SeoData> = {
  '/recherche': {
    title: 'Rechercher un praticien de santé à Rabat — MEDICACLIC',
    description:
      "Parcourez l'annuaire MEDICACLIC et trouvez un médecin, un laboratoire d'analyses, un centre de radiologie ou une pharmacie à Rabat, puis réservez votre rendez-vous en ligne.",
    keywords:
      'recherche praticien Rabat, annuaire santé Rabat, médecin Rabat, laboratoire analyses Rabat',
    canonicalPath: '/recherche',
  },
  '/mentions-legales': {
    title: 'Mentions légales — MEDICACLIC',
    description:
      'Mentions légales du site MEDICACLIC : éditeur, hébergement, propriété intellectuelle et responsabilité de la plateforme de mise en relation santé à Rabat.',
    keywords: 'mentions légales, MEDICACLIC, éditeur, hébergement, Rabat',
    canonicalPath: '/mentions-legales',
  },
  '/cgu': {
    title: "Conditions générales d'utilisation — MEDICACLIC",
    description:
      "Conditions générales d'utilisation et de vente de MEDICACLIC : accès au service, prise de rendez-vous, obligations des utilisateurs et des praticiens partenaires, formules et responsabilités.",
    keywords: 'conditions générales, CGU CGV, MEDICACLIC, praticien partenaire',
    canonicalPath: '/cgu',
  },
  '/politique-de-confidentialite': {
    title: 'Politique de confidentialité — MEDICACLIC',
    description:
      'Politique de confidentialité de MEDICACLIC : données personnelles collectées, finalités du traitement, durée de conservation, sécurité et droits des utilisateurs (Loi 09-08).',
    keywords: 'politique de confidentialité, données personnelles, Loi 09-08, MEDICACLIC',
    canonicalPath: '/politique-de-confidentialite',
  },
};

// Résout les données SEO correspondant au chemin courant, en créant un titre
// et une description pertinents pour les pages ville / quartier / spécialité.
export function resolveSeo(pathname: string): SeoData {
  const cleanPath = pathname.replace(/\/+$/, '') || '/';

  if (cleanPath === '/') return HOME_SEO;
  if (STATIC_SEO[cleanPath]) return STATIC_SEO[cleanPath];

  // Fiche praticien : titre générique (affiné par la page une fois les données chargées).
  if (cleanPath.startsWith('/praticien')) {
    return {
      title: `Fiche praticien — ${SITE_NAME}`,
      description:
        "Consultez les informations pratiques et les disponibilités d'un praticien de santé à Rabat, puis réservez votre rendez-vous en ligne sur MEDICACLIC.",
      keywords: 'fiche praticien, rendez-vous médical Rabat, praticien de santé Maroc',
      canonicalPath: cleanPath,
    };
  }

  // Pages de résultats : /rabat/<quartier> et /rabat/<quartier>/<spécialité>.
  const segments = cleanPath.split('/').filter(Boolean);
  if ((segments.length === 2 || segments.length === 3) && segments[0] === CITY_SLUG) {
    const district = findByNameSlug(districts, segments[1]);
    const specialty = findByNameSlug(specialties, segments[2]);
    if (district) {
      const title = specialty
        ? `${specialty} à ${district}, Rabat — ${SITE_NAME}`
        : `Praticiens de santé à ${district}, Rabat — ${SITE_NAME}`;
      const description = specialty
        ? `Trouvez un ${specialty.toLowerCase()} à ${district}, Rabat et prenez rendez-vous en ligne. Praticiens vérifiés, disponibilités à jour et confirmation rapide sur ${SITE_NAME}.`
        : `Annuaire des praticiens de santé à ${district}, Rabat : médecins, laboratoires, radiologie et pharmacies. Comparez les offres et réservez votre rendez-vous en ligne sur ${SITE_NAME}.`;
      return {
        title,
        description,
        keywords: `${specialty || 'praticiens'}, ${district} Rabat, rendez-vous médical Rabat, médecin ${district}`,
        canonicalPath: cleanPath,
      };
    }
  }

  // Route inconnue / page introuvable : à ne pas indexer.
  return {
    title: `Page introuvable — ${SITE_NAME}`,
    description: "La page demandée n'existe pas ou n'est plus disponible.",
    keywords: '',
    canonicalPath: cleanPath,
    noindex: true,
  };
}